import { useState, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, ClipboardList, Search } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldLabel, FieldDescription } from '@/components/ui/field';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useProcedureTemplates } from '@/lib/hooks/use-procedure-templates';
import { useAdminProcedureTemplates } from '@/lib/hooks/use-admin-procedure-templates';
import { useTranslation } from 'react-i18next';

const defaultUsers = [];

export const FORM_FOLDER_EDIT_ID = 'form-folder-edit';

/**
 * FormFolderEdit - Unified edit form for folders, subfolders, photos, and procedures
 * @param {Object} item - The item to edit
 * @param {string} itemType - The type: 'folder', 'intervention', 'photo', 'procedure_report'
 * @param {function} onSubmit - Submit handler
 */
export default function FormFolderEdit({ item, itemType = 'folder', onSubmit, users = defaultUsers, status = '' }) {
    const { t } = useTranslation();
    const { templates: procedureTemplates } = useProcedureTemplates();
    const { templates: adminTemplates, companyName: adminCompanyName } = useAdminProcedureTemplates();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        reference: '',
        user: '',
        interventionDate: '',
        address: '',
        comments: '',
        procedures: [],
    });
    const [userPopoverOpen, setUserPopoverOpen] = useState(false);
    const [procedureSearch, setProcedureSearch] = useState('');

    // Fusionner les procédures standard et admin (procédures-types)
    const allTemplates = useMemo(() => {
        const standardWithType = procedureTemplates.map((t) => ({ ...t, _isAdmin: false }));
        const adminWithType = adminTemplates.map((t) => ({ ...t, _isAdmin: true }));
        const seen = new Set();
        return [...standardWithType, ...adminWithType].filter((t) => {
            if (seen.has(t.uniq_id)) return false;
            seen.add(t.uniq_id);
            return true;
        });
    }, [procedureTemplates, adminTemplates]);

    const filteredProcedures = useMemo(() => {
        const validTemplates = allTemplates.filter((t) => t.configuration?.sections?.length > 0);
        if (!procedureSearch.trim()) return validTemplates;
        const search = procedureSearch.toLowerCase();
        return validTemplates.filter(
            (t) => t.name.toLowerCase().includes(search) || formData.procedures.includes(t.uniq_id),
        );
    }, [allTemplates, procedureSearch, formData.procedures]);

    const toggleProcedure = (uniqId) => {
        setFormData((prev) => ({
            ...prev,
            procedures: prev.procedures.includes(uniqId)
                ? prev.procedures.filter((id) => id !== uniqId)
                : [...prev.procedures, uniqId],
        }));
    };

    // Update form when item changes
    useEffect(() => {
        if (item) {
            setFormData({
                name: item.name || '',
                description: item.description || '',
                reference: item.reference || '',
                user: item.user || item.assignedTo || item.raw?.user?.uniq_id || item.raw?.user_id || '',
                interventionDate: item.interventionDate || item.raw?.intervention_date || '',
                address: item.address || item.raw?.street_1 || '',
                comments: item.comments || item.raw?.notes || '',
                procedures: (item.raw?.mandatory_procedures ?? []).map((p) => p.uniq_id ?? p.id ?? p),
            });
        }
    }, [item]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            return;
        }
        onSubmit({ ...formData, id: item?.id, type: itemType, status });
    };

    const getLabels = () => {
        switch (itemType) {
            case 'photo':
                return {
                    name: t('folders:nomDeLaPhoto'),
                    placeholder: t('folders:exPhotoFacadePrincipale'),
                    info: t('folders:modifiezLesInformationsDeCettePhotoHorodatee'),
                };
            case 'procedure_report':
                return {
                    name: t('folders:nomDuProcesVerbal'),
                    placeholder: t('folders:exPvReceptionTravaux'),
                    info: t('folders:modifiezLesInformationsDeCeProcesVerbal'),
                };
            case 'intervention':
                return {
                    name: t('folders:nomDuSousDossier2'),
                    placeholder: t('folders:exPhase1'),
                    info: t('folders:unDossierVousPermetDOrganiserVosPhotos2'),
                };
            default:
                return {
                    name: t('folders:nomDuDossier2'),
                    placeholder: t('folders:exChantierVillaDupont'),
                    info: t('folders:unDossierVousPermetDOrganiserVosPhotos2'),
                };
        }
    };

    const labels = getLabels();
    const showFullForm = itemType === 'folder' || itemType === 'intervention';

    return (
        <form id={FORM_FOLDER_EDIT_ID} onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {/* Nom */}
            <Field>
                <FieldLabel required>{labels.name}</FieldLabel>
                <Input
                    type="text"
                    placeholder={labels.placeholder}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                />
            </Field>

            {/* Description */}
            <Field>
                <FieldLabel>{t('folders:description')}</FieldLabel>
                <Textarea
                    placeholder={t('folders:ajouterUneDescription')}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={3}
                />
            </Field>

            {/* Fields only for folders/subfolders */}
            {showFullForm && (
                <>
                    {/* Référence */}
                    <Field>
                        <FieldLabel>{t('folders:reference')}</FieldLabel>
                        <Input
                            type="text"
                            placeholder={t('folders:exRef12345')}
                            value={formData.reference}
                            onChange={(e) => handleChange('reference', e.target.value)}
                        />
                    </Field>

                    {/* Responsable - affiché seulement si des collaborateurs sont disponibles */}
                    {users && users.length > 0 && (
                        <Field>
                            <FieldLabel>{t('folders:responsable')}</FieldLabel>
                            <Popover open={userPopoverOpen} onOpenChange={setUserPopoverOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        color="neutral"
                                        role="combobox"
                                        aria-expanded={userPopoverOpen}
                                        className="w-full justify-between font-normal"
                                    >
                                        {formData.user
                                            ? (() => {
                                                  const selectedUser = users.find(
                                                      (user) => String(user.uniq_id) === formData.user,
                                                  );
                                                  return selectedUser
                                                      ? `${selectedUser.first_name} ${selectedUser.last_name}`.trim()
                                                      : formData.user;
                                              })()
                                            : t('folders:selectionnerUnResponsable')}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="p-0"
                                    align="start"
                                    style={{ width: 'var(--radix-popover-trigger-width)' }}
                                    collisionPadding={{ top: 20, bottom: 20, left: 10, right: 10 }}
                                >
                                    <Command
                                        filter={(value, search) => {
                                            const s = search.toLowerCase();
                                            const v = value.toLowerCase();
                                            const atIdx = v.lastIndexOf('@');
                                            let name, emailLocal, emailDomain;
                                            if (atIdx > -1) {
                                                const sp = v.lastIndexOf(' ', atIdx);
                                                name = v.substring(0, sp).trim();
                                                const email = v.substring(sp + 1).trim();
                                                emailLocal = email.substring(0, email.indexOf('@'));
                                                emailDomain = email.substring(email.indexOf('@') + 1);
                                            } else {
                                                name = v;
                                                emailLocal = '';
                                                emailDomain = '';
                                            }
                                            if (name.startsWith(s)) return 1;
                                            if (emailLocal.startsWith(s)) return 0.9;
                                            if (name.split(' ').some((w) => w.startsWith(s))) return 0.8;
                                            if (name.includes(s)) return 0.6;
                                            if (emailLocal.includes(s)) return 0.5;
                                            if (emailDomain.includes(s)) return 0.3;
                                            return 0;
                                        }}
                                    >
                                        <CommandInput placeholder={t('folders:rechercherUnResponsable')} />
                                        <CommandList>
                                            <CommandEmpty>{t('folders:aucunResponsableTrouve')}</CommandEmpty>
                                            <CommandGroup>
                                                {users
                                                    .filter(
                                                        (user, index, self) =>
                                                            index === self.findIndex((u) => u.uniq_id === user.uniq_id),
                                                    )
                                                    .map((user) => {
                                                        const fullName = `${user.first_name} ${user.last_name}`.trim();
                                                        const searchValue = `${fullName} ${user.email || ''}`.trim();
                                                        return (
                                                            <CommandItem
                                                                key={user.uniq_id}
                                                                value={searchValue || user.uniq_id}
                                                                onSelect={() => {
                                                                    handleChange('user', String(user.uniq_id));
                                                                    setUserPopoverOpen(false);
                                                                }}
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        'mr-2 h-4 w-4 shrink-0',
                                                                        formData.user === String(user.uniq_id)
                                                                            ? 'opacity-100'
                                                                            : 'opacity-0',
                                                                    )}
                                                                />
                                                                <div className="flex flex-col">
                                                                    <span>{fullName || user.uniq_id}</span>
                                                                    {user.email && (
                                                                        <span className="text-xs text-muted-foreground">
                                                                            {user.email}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </CommandItem>
                                                        );
                                                    })}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        </Field>
                    )}

                    {/* Date d'intervention */}
                    <Field>
                        <FieldLabel>{t('folders:dateDuSousDossier')}</FieldLabel>
                        <Input
                            type="date"
                            value={formData.interventionDate}
                            onChange={(e) => handleChange('interventionDate', e.target.value)}
                        />
                    </Field>

                    {/* Adresse */}
                    <Field>
                        <FieldLabel>{t('folders:adresse')}</FieldLabel>
                        <Input
                            type="text"
                            placeholder={t('folders:ex123RueDeLaPaix75001Paris')}
                            value={formData.address}
                            onChange={(e) => handleChange('address', e.target.value)}
                        />
                    </Field>
                </>
            )}

            {/* Commentaires */}
            <Field>
                <FieldLabel>{t('folders:commentaires')}</FieldLabel>
                <Textarea
                    placeholder={t('folders:ajouterDesCommentaires')}
                    value={formData.comments}
                    onChange={(e) => handleChange('comments', e.target.value)}
                    rows={3}
                />
            </Field>

            {/* Procédures obligatoires (sous-dossiers uniquement) */}
            {itemType === 'intervention' && allTemplates.length > 0 && (
                <Field>
                    <div className="flex items-center justify-between">
                        <FieldLabel>{t('folders:proceduresObligatoires2')}</FieldLabel>
                        {formData.procedures.length > 0 && (
                            <span className="text-sm text-muted-foreground">
                                {formData.procedures.length} {t('folders:selectionnee')}{formData.procedures.length > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder={t('folders:rechercherUneProcedure')}
                            value={procedureSearch}
                            onChange={(e) => setProcedureSearch(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <div className="space-y-2 max-h-[240px] overflow-y-auto">
                        {filteredProcedures.map((template) => {
                            const isChecked = formData.procedures.includes(template.uniq_id);
                            return (
                                <div
                                    key={template.uniq_id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => toggleProcedure(template.uniq_id)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleProcedure(template.uniq_id);
                                        }
                                    }}
                                    className={cn(
                                        'w-full flex items-center justify-between px-3.5 py-3 rounded-xl border-2 transition-colors cursor-pointer',
                                        isChecked ? 'border-success bg-success/5' : 'border-border bg-muted',
                                    )}
                                >
                                    <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                        <ClipboardList
                                            className={cn(
                                                'h-4 w-4 shrink-0 mt-0.5',
                                                isChecked ? 'text-success' : 'text-muted-foreground',
                                            )}
                                        />
                                        <div className="flex flex-col gap-1 min-w-0">
                                            <TooltipProvider delayDuration={300}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="text-sm text-foreground text-left truncate">
                                                            {template.name}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top">
                                                        <p>{template.name}</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                            {template._isAdmin && (
                                                <span className="text-[10px] font-medium text-badge-invitation bg-badge-invitation/10 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap self-start">
                                                    {template.creator?.company || adminCompanyName || t('folders:admin')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className={cn(
                                            'grid place-content-center h-5 w-5 shrink-0 rounded-full border-2 transition-colors',
                                            isChecked
                                                ? 'bg-success border-success text-success-foreground'
                                                : 'border-muted-foreground/30',
                                        )}
                                    >
                                        {isChecked && <Check className="h-3 w-3" />}
                                    </div>
                                </div>
                            );
                        })}
                        {filteredProcedures.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-3">{t('folders:aucuneProcedureTrouvee')}</p>
                        )}
                    </div>
                </Field>
            )}

            {/* Info text */}
            <FieldDescription>{labels.info}</FieldDescription>
        </form>
    );
}
